import React, { useState, useEffect } from 'react';
import Modal from '../../../../../../common/Modal';

const AddLessonModal = ({ isOpen, onClose, onSubmit, sectionId }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('video_url');
  const [contentUrl, setContentUrl] = useState('');
  const [embedCode, setEmbedCode] = useState('');
  const [contentText, setContentText] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [duration, setDuration] = useState('');
  const [isFree, setIsFree] = useState(false); // القيمة الافتراضية هي غير مجاني (مدفوع)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  // إعادة تعيين النموذج عند الفتح أو الإغلاق
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setType('video_url');
      setContentUrl('');
      setEmbedCode('');
      setContentText('');
      setVideoFile(null);
      setDuration('');
      setIsFree(false);
      setUploadProgress(0);
      setError('');
    }
  }, [isOpen]);

  // إغلاق النافذة
  const handleClose = () => {
    onClose();
  };

  // تقديم النموذج
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // التحقق من البيانات
    if (!title.trim()) {
      setError('يرجى إدخال عنوان الدرس');
      return;
    }
    
    if (type === 'video_url' && !contentUrl.trim()) {
      setError('يرجى إدخال رابط الفيديو');
      return;
    }
    
    if (type === 'video_file' && !videoFile) {
      setError('يرجى اختيار ملف فيديو');
      return;
    }
    
    if (type === 'text' && !contentText.trim()) {
      setError('يرجى إدخال محتوى الدرس');
      return;
    }
    
    if (type === 'video_embed' && !embedCode.trim()) {
      setError('يرجى إدخال كود الإمبد');
      return;
    }
    
    try {
      setLoading(true);
      
      // تجهيز بيانات الدرس
      const lessonData = {
        title,
        description,
        type,
        is_free: isFree,
        duration: (type === 'video_url' || type === 'video_file' || type === 'video_embed') ? (duration || '0') : '0'
      };
      
      if (type === 'video_url') {
        lessonData.content_url = contentUrl;
      } else if (type === 'video_embed') {
        lessonData.content_url = embedCode;
      } else if (type === 'video_file') {
        lessonData.video_file = videoFile;
      } else if (type === 'text') {
        lessonData.content_text = contentText;
      }
      
      // إرسال البيانات مع تتبع التقدم
      await onSubmit(lessonData, (progress) => {
        setUploadProgress(progress);
      });
      
      // إغلاق النافذة بعد الإضافة
      handleClose();
    } catch (err) {
      // معالجة خطأ إضافة الدرس
      setError('حدث خطأ أثناء إضافة الدرس. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };



  // تغيير ملف الفيديو
  const handleVideoFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // تم اختيار ملف الفيديو
      
      // التحقق من حجم الملف (أقل من 400 ميجا)
      const maxSize = 400 * 1024 * 1024; // 400MB
      if (file.size > maxSize) {
        setError(`حجم الملف كبير جداً. الحد الأقصى هو 400 ميجابايت. حجم الملف الحالي: ${(file.size / (1024 * 1024)).toFixed(2)} ميجابايت`);
        return;
      }
      
      setVideoFile(file);
      setError(''); // مسح أي خطأ سابق
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="إضافة درس جديد">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        )}
        
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            عنوان الدرس *
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
            placeholder="أدخل عنوان الدرس"
          />
        </div>
        
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            وصف الدرس
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
            placeholder="أدخل وصف الدرس (اختياري)"
          />
        </div>
        
        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            نوع الدرس *
          </label>
          <select
            id="type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="video_url">فيديو من رابط</option>
            <option value="video_embed">رابط امبد</option>
            <option value="video_file">فيديو من ملف</option>
            <option value="text">نص</option>
          </select>
        </div>
        
        {type === 'video_url' && (
          <div>
            <label htmlFor="contentUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              رابط الفيديو *
            </label>
            <input
              type="text"
              id="contentUrl"
              value={contentUrl}
              onChange={(e) => setContentUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="أدخل رابط الفيديو (YouTube, Vimeo, إلخ)"
            />
          </div>
        )}
        
        {type === 'video_file' && (
          <div>
            <label htmlFor="videoFile" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              ملف الفيديو *
            </label>
            <input
              type="file"
              id="videoFile"
              onChange={handleVideoFileChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              accept="video/*"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              أنواع الملفات المسموح بها: MP4, WebM, MOV, AVI
            </p>
            {videoFile && (
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                الملف المختار: {videoFile.name}
              </p>
            )}
          </div>
        )}
        
        {type === 'text' && (
          <div>
            <label htmlFor="contentText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              محتوى الدرس *
            </label>
            <textarea
              id="contentText"
              value={contentText}
              onChange={(e) => setContentText(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="أدخل محتوى الدرس"
            />
          </div>
        )}
        
        {type === 'video_embed' && (
          <div>
            <label htmlFor="embedCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              كود الإمبد *
            </label>
            <textarea
              id="embedCode"
              value={embedCode}
              onChange={(e) => setEmbedCode(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder='أدخل كود الإمبد (مثال: <iframe src="https://www.example.com/embed" ...></iframe>)'
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              أدخل كود الإمبد كاملاً بما في ذلك وسوم iframe
            </p>
          </div>
        )}
        
        {/* خيار مجاني */}
        <div className="flex items-center justify-between border p-3 rounded-md bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <div className="flex flex-col">
            <label htmlFor="is_free" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              درس مجاني (معاينة)
            </label>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              إذا كان مفعلاً، سيتمكن المستخدمون من مشاهدة هذا الدرس بدون شراء الدورة
            </div>
          </div>
          <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out rounded-full cursor-pointer">
            <input
              type="checkbox"
              id="is_free"
              checked={isFree}
              onChange={(e) => setIsFree(e.target.checked)}
              className="absolute w-0 h-0 opacity-0"
            />
            <label
              htmlFor="is_free"
              className={`absolute left-0 right-0 block h-6 rounded-full transition-colors duration-200 ease-in cursor-pointer ${
                isFree ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`absolute left-0 bottom-0 top-0 block w-6 h-6 mt-0 mb-0 ml-0 rounded-full bg-white shadow transform transition-transform duration-200 ease-in ${
                  isFree ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </label>
          </div>
        </div>
        
        {(type === 'video_url' || type === 'video_file' || type === 'video_embed') && (
          <div>
            <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              مدة الفيديو
              <span className="mr-2 text-xs text-gray-500 dark:text-gray-400">(بالدقائق)</span>
            </label>
            <input
              type="number"
              id="duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="أدخل مدة الفيديو بالدقائق (مثال: 15)"
              min="0"
            />
          </div>
        )}
        
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'جاري الإضافة...' : 'إضافة الدرس'} {uploadProgress > 0 && uploadProgress < 100 && `(${uploadProgress}%)` }
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddLessonModal;
